/**
 * Structured-payload editor for kind=evidence_request templates.
 * Friendly labels for tenant admins; structured_payload JSON shape stays
 * unchanged. Per-card Advanced toggle exposes the stable identifier
 * (`key`) when someone needs to override the auto-generated value.
 */

import { useRef, useState } from 'react'
import {
  ArrowDown,
  ArrowUp,
  FileText,
  ImageIcon,
  Loader2,
  Plus,
  Trash2,
  Upload,
} from 'lucide-react'
import { toast } from 'sonner'

import { Badge } from '@/shared/components/ui/badge'
import { Button } from '@/shared/components/ui/button'
import { Input } from '@/shared/components/ui/input'
import { Label } from '@/shared/components/ui/label'
import { Separator } from '@/shared/components/ui/separator'
import { EmailEditor } from '@/admin/components/email-editor'
import { useUploadFile } from '@/admin/hooks/use-products'

import { slugify } from './slug'
import type {
  DocumentOption,
  DocumentRequirement,
  EvidenceRequestPayload,
  PhotoRequirement,
  Requirement,
} from './types'

interface Props {
  value: EvidenceRequestPayload
  onChange: (next: EvidenceRequestPayload) => void
}

export function RequirementsEditor({ value, onChange }: Props) {
  const requirements = value.requirements ?? []
  const tips = value.photo_tips ?? []
  const outro = value.outro_html ?? ''

  const setRequirements = (next: Requirement[]) =>
    onChange({ ...value, requirements: next })
  const setTips = (next: string[]) => onChange({ ...value, photo_tips: next })
  const setOutro = (next: string) => onChange({ ...value, outro_html: next })

  const addRequirement = (kind: 'document' | 'photo') => {
    // Starts blank (the placeholder guides) so typing never appends to a
    // pre-filled label.
    const base = {
      key: `${kind}_${requirements.length + 1}`,
      label: '',
      description: '',
      min: 1,
      max: kind === 'document' ? 1 : 3,
    }
    const next: Requirement =
      kind === 'document'
        ? {
            ...base,
            kind: 'document',
            options: [{ key: 'any', label: 'Any of the above' }],
          }
        : { ...base, kind: 'photo', example_media_urls: [] }
    setRequirements([...requirements, next])
  }

  const updateRequirement = (
    index: number,
    patch: Partial<Requirement>,
    opts: { autoKey?: boolean } = {},
  ) => {
    const next = [...requirements]
    const merged = { ...next[index], ...patch } as Requirement
    // Auto-key from label unless someone has manually edited the key in Advanced.
    if (opts.autoKey && patch.label != null) {
      merged.key = slugify(patch.label) || merged.key
    }
    next[index] = merged
    setRequirements(next)
  }

  const moveRequirement = (index: number, dir: -1 | 1) => {
    const target = index + dir
    if (target < 0 || target >= requirements.length) return
    const next = [...requirements]
    ;[next[index], next[target]] = [next[target], next[index]]
    setRequirements(next)
  }

  const removeRequirement = (index: number) => {
    setRequirements(requirements.filter((_, i) => i !== index))
  }

  return (
    <div className="space-y-6">
      <PhotoTipsSection tips={tips} onChange={setTips} />

      <Separator />

      <section className="space-y-3">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <div>
            <h3 className="text-sm font-semibold text-slate-900">Items to collect</h3>
            <p className="text-xs text-slate-500 mt-0.5">
              Each item becomes its own card on the upload page. Use the
              up/down arrows to reorder.
            </p>
          </div>
          <div className="flex gap-2">
            <Button size="sm" variant="outline" onClick={() => addRequirement('document')}>
              <FileText size={14} />
              Add document
            </Button>
            <Button size="sm" variant="outline" onClick={() => addRequirement('photo')}>
              <ImageIcon size={14} />
              Add photo type
            </Button>
          </div>
        </div>

        {requirements.length === 0 ? (
          <div className="text-center py-8 bg-slate-50 border border-dashed border-slate-200 rounded-lg">
            <p className="text-sm text-slate-500">
              Add at least one item so people know what to upload.
            </p>
          </div>
        ) : (
          <div className="space-y-3">
            {requirements.map((req, i) => (
              <RequirementCard
                key={i}
                requirement={req}
                index={i}
                isFirst={i === 0}
                isLast={i === requirements.length - 1}
                onChange={(patch, opts) => updateRequirement(i, patch, opts)}
                onMoveUp={() => moveRequirement(i, -1)}
                onMoveDown={() => moveRequirement(i, 1)}
                onRemove={() => removeRequirement(i)}
              />
            ))}
          </div>
        )}
      </section>

      <Separator />

      <section className="space-y-2">
        <EmailEditor
          label="Closing message (optional)"
          value={outro}
          onChange={setOutro}
          minHeight={80}
          maxHeight={120}
          htmlRows={8}
        />
        <p className="text-xs text-slate-500">
          Shown after the upload checklist on both the email and the upload
          page. Good place for legal notes, processing time expectations, or
          a sign-off.
        </p>
      </section>
    </div>
  )
}

// ─── Photo tips ────────────────────────────────────────────────────

function PhotoTipsSection({
  tips,
  onChange,
}: {
  tips: string[]
  onChange: (next: string[]) => void
}) {
  const update = (i: number, val: string) => {
    const next = [...tips]
    next[i] = val
    onChange(next)
  }
  return (
    <section className="space-y-2">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-sm font-semibold text-slate-900">Photo tips</h3>
          <p className="text-xs text-slate-500 mt-0.5">
            Quick guidelines shown above the upload area. Helps people send
            you usable shots.
          </p>
        </div>
        <Button size="sm" variant="outline" onClick={() => onChange([...tips, ''])}>
          <Plus size={14} />
          Add tip
        </Button>
      </div>
      {tips.length > 0 && (
        <ul className="space-y-1.5">
          {tips.map((tip, i) => (
            <li key={i} className="flex items-center gap-2">
              <Input
                value={tip}
                onChange={(e) => update(i, e.target.value)}
                placeholder="e.g. Take photos in daylight"
              />
              <Button
                size="icon-sm"
                variant="ghost"
                onClick={() => onChange(tips.filter((_, j) => j !== i))}
                aria-label="Remove tip"
              >
                <Trash2 size={14} />
              </Button>
            </li>
          ))}
        </ul>
      )}
    </section>
  )
}

// ─── Single requirement card ───────────────────────────────────────

function RequirementCard({
  requirement,
  index,
  isFirst,
  isLast,
  onChange,
  onMoveUp,
  onMoveDown,
  onRemove,
}: {
  requirement: Requirement
  index: number
  isFirst: boolean
  isLast: boolean
  onChange: (
    patch: Partial<Requirement>,
    opts?: { autoKey?: boolean },
  ) => void
  onMoveUp: () => void
  onMoveDown: () => void
  onRemove: () => void
}) {
  const isDoc = requirement.kind === 'document'

  return (
    <div className="bg-white border border-slate-200 rounded-lg p-4">
      <header className="flex items-center justify-between gap-2 mb-3">
        <div className="flex items-center gap-2">
          <Badge variant="secondary">
            <span className="flex items-center gap-1.5">
              {isDoc ? <FileText size={12} /> : <ImageIcon size={12} />}
              {isDoc ? 'Document' : 'Photo'} · #{index + 1}
            </span>
          </Badge>
        </div>
        <div className="flex gap-1">
          <Button size="icon-sm" variant="ghost" disabled={isFirst} onClick={onMoveUp}>
            <ArrowUp size={14} />
          </Button>
          <Button size="icon-sm" variant="ghost" disabled={isLast} onClick={onMoveDown}>
            <ArrowDown size={14} />
          </Button>
          <Button size="icon-sm" variant="ghost" onClick={onRemove} aria-label="Remove">
            <Trash2 size={14} />
          </Button>
        </div>
      </header>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <div className="md:col-span-2">
          <Label>Label</Label>
          <Input
            value={requirement.label}
            onChange={(e) =>
              onChange(
                { label: e.target.value } as Partial<Requirement>,
                // Without the (now-removed) Advanced toggle there's no way
                // for the admin to set a custom key, so we always re-key
                // from the label.
                { autoKey: true },
              )
            }
            placeholder={
              isDoc ? 'e.g. Proof of Ownership' : 'e.g. Front of House'
            }
          />
        </div>
        <div className="md:col-span-2">
          <Label>Helper text (optional)</Label>
          <Input
            value={requirement.description ?? ''}
            onChange={(e) =>
              onChange({ description: e.target.value } as Partial<Requirement>)
            }
            placeholder="Shown below the label - keeps the uploader on track"
          />
        </div>
        <div>
          <Label>Minimum required</Label>
          <Input
            type="number"
            min={0}
            value={requirement.min}
            onChange={(e) =>
              onChange({ min: Number(e.target.value) || 0 } as Partial<Requirement>)
            }
          />
          <p className="text-xs text-slate-500 mt-1">
            0 = optional. Otherwise the recipient can't submit until they
            upload this many.
          </p>
        </div>
        <div>
          <Label>Maximum allowed (optional)</Label>
          <Input
            type="number"
            min={1}
            value={requirement.max ?? ''}
            onChange={(e) => {
              const v = e.target.value === '' ? null : Number(e.target.value)
              onChange({ max: v } as Partial<Requirement>)
            }}
            placeholder="Leave blank for no limit"
          />
        </div>
      </div>

      {isDoc && (
        <DocumentOptionsEditor
          options={(requirement as DocumentRequirement).options ?? []}
          onChange={(next) =>
            onChange({ options: next } as Partial<DocumentRequirement>)
          }
        />
      )}

      {!isDoc && (
        <PhotoExamplesEditor
          urls={(requirement as PhotoRequirement).example_media_urls ?? []}
          onChange={(next) =>
            onChange({ example_media_urls: next } as Partial<PhotoRequirement>)
          }
        />
      )}
    </div>
  )
}

// ─── Per-kind sub-editors ──────────────────────────────────────────

// Curated presets that cover ~80% of evidence-packet use cases. Picking
// one fills in label + key; "Custom…" leaves the label free-text for
// anything bespoke (e.g. a tenant-specific document type).
const DOCUMENT_TYPE_PRESETS: Array<{ label: string; key: string }> = [
  { label: 'Tax bill', key: 'tax_bill' },
  { label: 'Utility bill', key: 'utility_bill' },
  { label: 'Mortgage statement', key: 'mortgage_statement' },
  { label: 'Insurance certificate', key: 'insurance_certificate' },
  { label: 'Bank statement', key: 'bank_statement' },
  { label: 'Receipt / Proof of purchase', key: 'receipt' },
  { label: 'Driver license / ID', key: 'identification' },
  { label: 'Bill of sale', key: 'bill_of_sale' },
  { label: 'Lease agreement', key: 'lease_agreement' },
  { label: 'Photo of installed product', key: 'installation_photo' },
  { label: 'Photo of damage', key: 'damage_photo' },
  { label: 'Inspection report', key: 'inspection_report' },
  { label: 'Other document', key: 'other_document' },
]

const CUSTOM_PRESET_VALUE = '__custom__'

function DocumentOptionsEditor({
  options,
  onChange,
}: {
  options: DocumentOption[]
  onChange: (next: DocumentOption[]) => void
}) {
  const update = (i: number, patch: Partial<DocumentOption>) => {
    const next = [...options]
    next[i] = { ...next[i], ...patch }
    // Auto-key from label when the label changes.
    if (patch.label != null) {
      next[i].key = slugify(patch.label) || next[i].key
    }
    onChange(next)
  }
  // Pick from preset → fill in both label + key. "Custom…" drops to
  // free text without overwriting any existing label.
  const pickPreset = (i: number, presetLabel: string) => {
    if (presetLabel === CUSTOM_PRESET_VALUE) {
      // Mark as custom by clearing label so the input becomes editable.
      update(i, { label: options[i].label || '' })
      return
    }
    const preset = DOCUMENT_TYPE_PRESETS.find((p) => p.label === presetLabel)
    if (!preset) return
    update(i, { label: preset.label })
  }

  return (
    <div className="mt-4 space-y-2">
      <div className="flex items-center justify-between">
        <div>
          <Label className="mb-0">Acceptable document types</Label>
          <p className="text-xs text-slate-500 mt-0.5">
            People uploading pick one of these before attaching their file.
            Choose from the preset list or pick "Custom…" to type your own.
          </p>
        </div>
        <Button
          size="sm"
          variant="outline"
          onClick={() =>
            onChange([
              ...options,
              { key: `option_${options.length + 1}`, label: '' },
            ])
          }
        >
          <Plus size={14} />
          Add option
        </Button>
      </div>
      {options.length === 0 ? (
        <p className="text-xs text-rose-600">
          Add at least one acceptable document type.
        </p>
      ) : (
        <ul className="space-y-1.5">
          {options.map((opt, i) => {
            const isPreset = DOCUMENT_TYPE_PRESETS.some(
              (p) => p.label === opt.label,
            )
            const presetValue = isPreset ? opt.label : CUSTOM_PRESET_VALUE
            return (
              <li
                key={i}
                className="grid grid-cols-[1fr_1fr_auto_auto] gap-2 items-center"
              >
                <select
                  value={presetValue}
                  onChange={(e) => pickPreset(i, e.target.value)}
                  className="h-9 rounded-md border border-slate-300 px-2 text-sm bg-white"
                >
                  <option value="" disabled>
                    Pick a type…
                  </option>
                  {DOCUMENT_TYPE_PRESETS.map((p) => (
                    <option key={p.label} value={p.label}>
                      {p.label}
                    </option>
                  ))}
                  <option value={CUSTOM_PRESET_VALUE}>Custom…</option>
                </select>
                <Input
                  value={opt.label}
                  onChange={(e) => update(i, { label: e.target.value })}
                  placeholder={isPreset ? opt.label : 'e.g. Property Tax Bill'}
                  disabled={isPreset}
                  className={isPreset ? 'bg-slate-50 text-slate-500' : ''}
                />
                <label className="text-xs text-slate-600 flex items-center gap-1.5 whitespace-nowrap">
                  <input
                    type="checkbox"
                    checked={opt.preferred ?? false}
                    onChange={(e) => update(i, { preferred: e.target.checked })}
                    className="rounded"
                  />
                  Preferred
                </label>
                <Button
                  size="icon-sm"
                  variant="ghost"
                  onClick={() => onChange(options.filter((_, j) => j !== i))}
                >
                  <Trash2 size={14} />
                </Button>
              </li>
            )
          })}
        </ul>
      )}
    </div>
  )
}

function PhotoExamplesEditor({
  urls,
  onChange,
}: {
  urls: string[]
  onChange: (next: string[]) => void
}) {
  const fileInputRef = useRef<HTMLInputElement>(null)
  const [draftUrl, setDraftUrl] = useState('')
  const [isDragging, setIsDragging] = useState(false)
  const upload = useUploadFile()
  // file_path -> file_url for just-uploaded images so the preview works
  // immediately (file_path alone is not a displayable URL).
  const [previewMap, setPreviewMap] = useState<Record<string, string>>({})

  const uploadFiles = async (files: File[]) => {
    const images = files.filter((f) => f.type.startsWith('image/'))
    if (images.length === 0) {
      toast.error('Pick an image file (JPEG or PNG).')
      return
    }
    const added: string[] = []
    for (const file of images) {
      try {
        const result = await upload.mutateAsync({
          file,
          use_case: 'PRODUCT_IMAGE',
        })
        const storedValue = result.file_path ?? result.file_url
        added.push(storedValue)
        if (result.file_path && result.file_url) {
          setPreviewMap((prev) => ({ ...prev, [result.file_path!]: result.file_url }))
        }
      } catch {
        toast.error(`Couldn't upload ${file.name}.`)
      }
    }
    if (added.length) onChange([...urls, ...added])
  }

  const handleFileInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files ?? [])
    if (files.length) void uploadFiles(files)
    if (fileInputRef.current) fileInputRef.current.value = ''
  }

  // Image paste - admins copy an image (or a screenshot) and paste it
  // straight into the dropzone instead of saving + re-uploading.
  const handlePaste = (e: React.ClipboardEvent<HTMLDivElement>) => {
    const files = Array.from(e.clipboardData.files)
    if (files.length) {
      e.preventDefault()
      void uploadFiles(files)
    }
  }

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault()
    setIsDragging(false)
    const files = Array.from(e.dataTransfer.files)
    if (files.length) void uploadFiles(files)
  }

  const addUrl = () => {
    const trimmed = draftUrl.trim()
    if (!trimmed) return
    onChange([...urls, trimmed])
    setDraftUrl('')
  }

  return (
    <div className="mt-4 space-y-3">
      <div>
        <Label className="mb-0">Example photos (optional)</Label>
        <p className="text-xs text-slate-500 mt-0.5">
          Shown faded with an "example" label so uploaders see what's
          expected. Drag, paste, click to upload, or paste a public image
          URL below.
        </p>
      </div>

      {/* Drop zone - accepts drag, click, or Cmd/Ctrl-V paste. */}
      <div
        tabIndex={0}
        onPaste={handlePaste}
        onDragOver={(e) => {
          e.preventDefault()
          setIsDragging(true)
        }}
        onDragLeave={() => setIsDragging(false)}
        onDrop={handleDrop}
        onClick={() => fileInputRef.current?.click()}
        className={
          'rounded-lg border border-dashed px-4 py-5 text-center cursor-pointer transition-colors focus:outline-none focus:ring-1 focus:ring-slate-400 ' +
          (isDragging
            ? 'border-slate-900 bg-slate-50'
            : 'border-slate-300 bg-slate-50/40 hover:bg-slate-50')
        }
      >
        {upload.isPending ? (
          <div className="flex items-center justify-center gap-2 text-sm text-slate-500">
            <Loader2 size={14} className="animate-spin" />
            Uploading…
          </div>
        ) : (
          <div className="flex flex-col items-center gap-1 text-slate-500">
            <Upload size={18} />
            <p className="text-sm">
              <span className="font-medium text-slate-900">Drop images</span>,
              click to choose, or paste with Cmd/Ctrl-V
            </p>
            <p className="text-[11px] text-slate-400">JPEG or PNG</p>
          </div>
        )}
        <input
          ref={fileInputRef}
          type="file"
          accept="image/jpeg,image/png"
          multiple
          onChange={handleFileInput}
          className="hidden"
        />
      </div>

      {/* Optional: link in an externally-hosted image instead of uploading. */}
      <div className="flex items-center gap-2">
        <Input
          value={draftUrl}
          onChange={(e) => setDraftUrl(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault()
              addUrl()
            }
          }}
          placeholder="…or paste an image URL and press Enter"
          className="flex-1"
        />
        <Button
          size="sm"
          variant="outline"
          onClick={addUrl}
          disabled={!draftUrl.trim()}
        >
          <Plus size={14} />
          Add URL
        </Button>
      </div>

      {urls.length > 0 && (
        <ul className="grid grid-cols-2 md:grid-cols-4 gap-2">
          {urls.map((url, i) => (
            <li
              key={i}
              className="group relative rounded-md overflow-hidden border border-slate-200 bg-slate-50 aspect-square"
            >
              <img
                src={previewMap[url] || url}
                alt={`Example ${i + 1}`}
                className="w-full h-full object-cover"
                onError={(e) => {
                  ;(e.currentTarget as HTMLImageElement).style.display = 'none'
                }}
              />
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation()
                  onChange(urls.filter((_, j) => j !== i))
                }}
                aria-label="Remove example"
                className="absolute top-1 right-1 w-6 h-6 rounded-full bg-white/90 hover:bg-white text-slate-700 flex items-center justify-center opacity-0 group-hover:opacity-100 transition shadow-sm"
              >
                <Trash2 size={12} />
              </button>
              <div className="absolute bottom-0 inset-x-0 bg-gradient-to-t from-black/60 to-transparent px-1.5 py-1 opacity-0 group-hover:opacity-100 transition">
                <span className="text-[10px] text-white font-mono truncate block">
                  {url.split('/').pop()}
                </span>
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}
